(ns practitest.frontend.summary.views
  (:require [clojure.string :as str]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [reagent.dom :as rdom]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components.selector :as selector]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.dashboard.card :as dashboard-card]
            [practitest.frontend.components2.dashboard.chart :as dashboard-chart]
            [practitest.frontend.components2.dashboard.dashboard-tab-menu :as dashboard-tab-menu]
            [practitest.frontend.components2.dashboard.dashboard-tab-modal :as dashboard-tab-modal]
            [practitest.frontend.components2.dashboard.helpers :as dashboard-helpers]
            [practitest.frontend.components2.dashboard.tab-events :as tab-events]
            [practitest.frontend.components2.dropdown :as dropdown]
            [practitest.frontend.components2.horizontal-scroll-strip :as hss]
            [practitest.frontend.components2.search-box :as search-box]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as app-subs]
            [practitest.frontend.summary.events :as events]
            [practitest.frontend.summary.subs :as subs]))

(defn- summary-tab [{:keys [tab active? last-tab?]}]
  (let [{:keys [id name private]} tab]
    [:div.tab.summary-tab
     {:class (when active? "active")}
     [overflow/overflow-with-mux-tooltip
      {:position "bottom"}
      (fn [props]
        [:a.summary-tab-name (merge props {:on-click #(re-frame/dispatch [::events/select-tab id])})
         name])
      name]
     (when private
       [:span.summary-tab-private (get-translation "dashboards.tab.private" "Private")])
     (when active?
       [dashboard-tab-menu/dashboard-tab-menu
        {:tab              tab
         :last-unarchived? last-tab?
         :on-edit          #(re-frame/dispatch [::tab-events/open-edit-modal events/db-key id])
         :on-clone         #(re-frame/dispatch [::tab-events/clone-tab id
                                                {:on-success [::events/tab-cloned]
                                                 :on-failure [::events/request-failed]}])
         :on-archive       #(re-frame/dispatch [::tab-events/archive-tab id
                                                {:on-success [::events/tab-removed id
                                                              (get-translation "dashboards.notify.archived"
                                                                               "The dashboard has been successfully archived.")]
                                                 :on-failure [::events/request-failed]}])
         :on-delete        #(re-frame/dispatch [::tab-events/delete-tab id
                                                {:on-success [::events/tab-removed id
                                                              (get-translation "dashboards.notify.deleted"
                                                                               "The dashboard tab was deleted.")]
                                                 :on-failure [::events/request-failed]}])}])]))

(defn- tabs-strip [tabs current-id]
  [hss/horizontal-scroll-strip {:active-key current-id}
   (into [:div.tabs-container]
         (for [tab tabs]
           ^{:key (:id tab)}
           [summary-tab {:tab       tab
                         :active?   (= (:id tab) current-id)
                         :last-tab? (<= (count tabs) 1)}]))])

;; "All" dropdown: search and jump to any tab
(defn- all-tabs-row [{:keys [tab active?]}]
  (let [{:keys [id name]} tab]
    [:div.summary-all-tabs-row
     {:class (when active? "active")}
     [dropdown/dropdown-menu-item
      {:icon     [icons/mux-dashboard-tab]
       :text     [overflow/overflow-with-mux-tooltip
                  {:position "right"}
                  (fn [props]
                    [:span.summary-all-tabs-name props name])
                  name]
       :on-click #(re-frame/dispatch [::events/select-tab id])}]]))

;; can-manage? - same permission as the Dashboard Tab List page (dashboard_private)
(defn- all-tabs-menu [tabs current-id can-manage?]
  (reagent/with-let [search (reagent/atom "")]
    (let [q          @search
          searching? (not (str/blank? q))
          rows       (cond->> tabs
                       searching? (filter #(fu/substring-search? (:name %) q)))
          project-id @(re-frame/subscribe [::app-subs/current-project-id])]
      [:div.m-dropdown-menu-body.summary-all-tabs
       [:div.summary-all-tabs-search
        [search-box/search-textbox
         {:placeholder    (get-translation "summary.tabs.search" "Search tabs")
          :value          q
          :on-change      #(reset! search (dom/event-value %))
          :on-clear-click #(reset! search "")}]]
       (if (seq rows)
         (into [:div.summary-all-tabs-list]
               (for [tab rows]
                 ^{:key (:id tab)}
                 [all-tabs-row {:tab tab :active? (= (:id tab) current-id)}]))
         [:div.summary-all-tabs-empty (get-translation "summary.tabs.no-results" "No tabs found")])
       [:div.summary-all-tabs-footer
        [dropdown/dropdown-menu-item
         {:text      (get-translation "summary.tabs.settings" "Dashboard Tabs Settings")
          :icon      [icons/mux-gear-outline]
          :href      (broutes/get-url "dashboards#index" {:p_id project-id})
          :disabled? (not can-manage?)
          :tooltip   (when-not can-manage?
                       (get-translation "summary.tabs.settings.no-permission"
                                        "You don't have permissions to manage dashboard tabs"))}]]])))

(defn- tab-bar []
  (let [tabs        @(re-frame/subscribe [::subs/field :tabs])
        current-id  @(re-frame/subscribe [::subs/field :current-tab-id])
        ;; Creating tabs and the Dashboard Tab List page share the dashboard_private permission
        can-create? (:can_create @(re-frame/subscribe [::subs/field :new-tab]))]
    [:div.summary-tab-bar
     [tabs-strip tabs current-id]
     [dropdown/dropdown-menu
      (fn [{:keys [toggle!]}]
        [buttons/button {:tertiary? true
                         :icon      [icons/mux-unordered-list]
                         :text      (get-translation "summary.tabs.all" "All")
                         :on-click  toggle!}])
      {:placement "bottom-end"}
      [all-tabs-menu tabs current-id can-create?]]
     [buttons/button {:tertiary? true
                      :icon      [icons/mux-reg-plus]
                      :text      (get-translation "dashboards.add-tab" "Add Dashboard Tab")
                      :disabled? (not can-create?)
                      :tooltip   (when-not can-create?
                                   (get-translation "dashboards.add-tab.no-permission"
                                                    "You don't have permissions to create dashboard tabs"))
                      :on-click  #(re-frame/dispatch [::tab-events/open-new-modal events/db-key])}]]))

(defn- tab-filter []
  (let [{:keys [label value options] :as tab-filter} @(re-frame/subscribe [::subs/field :tab-filter])
        selected (some #(when (= (:value %) value) %) options)]
    [:div.summary-tab-filter
     [icons/mux-tab-filter]
     [:span.summary-tab-filter-label (or label (get-translation "summary.tab-filter" "Tab Level Filter"))]
     [tooltip/mux-tooltip {:position "bottom"}
      (when-not tab-filter
        (get-translation "summary.tab-filter.not-set"
                         "No tab level filter is set for this tab. You can add one in Edit Tab."))
      (fn [props]
        [:div.summary-tab-filter-select props
         [selector/new-select
          {:items       (or options [])
           :key-fn      :value
           :label-fn    :label
           :value       selected
           :clearable?  true
           :disabled?   (nil? tab-filter)
           :placeholder (get-translation "summary.tab-filter.placeholder" "Select value")
           :on-change   #(re-frame/dispatch [::events/set-tab-filter (:value %)])}]])]]))

(defn- toolbar []
  (let [tab         @(re-frame/subscribe [::subs/current-tab])
        permissions @(re-frame/subscribe [::subs/field :permissions])
        can-add?    (:can_add_item permissions)]
    [:div.summary-toolbar
     [tab-filter]
     [:div.summary-toolbar-actions
      [dashboard-helpers/add-item-button {:editable? (:editable tab)
                                          :can-add?  can-add?
                                          :href      (:new_item_url tab)}]
      [buttons/button {:icon     [icons/mux-refresh]
                       :tooltip  (get-translation "summary.refresh" "Update dashboard now")
                       :on-click #(re-frame/dispatch [::events/reload-items])}]]]))

;; Loads its chart the first time it scrolls into view
(defn- card-body [_item]
  (let [observer (atom nil)]
    (reagent/create-class
      {:display-name "summary-card-body"
       :component-did-mount
       (fn [this]
         (let [item (second (reagent/argv this))
               obs  (js/IntersectionObserver.
                      (fn [entries]
                        (when (some #(.-isIntersecting %) entries)
                          (when-let [o @observer] (.disconnect o))
                          (re-frame/dispatch [::events/load-item item])))
                      #js {:rootMargin "200px"})]
           (reset! observer obs)
           (.observe obs (rdom/dom-node this))))
       :component-will-unmount
       (fn [_] (when-let [o @observer] (.disconnect o)))
       :reagent-render
       (fn [{:keys [id title editable edit_url] :as item}]
         (let [status @(re-frame/subscribe [::subs/item-status id])]
           [:div.summary-card-body
            (case status
              :loaded [dashboard-chart/chart #:chart{:id                    id
                                                     :edit-url              edit_url
                                                     :edit-disabled-tooltip (when-not editable (dashboard-helpers/no-permission-tooltip))}]
              :error  [dashboard-card/card {:title title}
                       [:div.summary-card-error
                        [:span (get-translation "summary.item.load-error" "Failed to load this dashboard item.")]
                        [buttons/button {:tertiary? true
                                         :text      (get-translation "generic.button.retry" "Retry")
                                         :on-click  #(re-frame/dispatch [::events/load-item item])}]]]
              [dashboard-card/card {:title title}
               [:div.summary-card-loading [spinners/mux-spinner]]])]))})))

(defn- summary-card [{:keys [width height] :as item}]
  [:div.summary-card
   {:class [(when (= width "double") "double-width")
            (when (= height "double") "double-height")]}
   [card-body item]])

(defn- cards-grid [items reload-key]
  (into [:div.summary-cards]
        (for [item items]
          ^{:key (str (:id item) "-" reload-key)}
          [summary-card item])))

(defn- empty-state []
  (let [tab @(re-frame/subscribe [::subs/current-tab])]
    [:div.summary-empty
     [icons/mux-empty-dashboard]
     [:strong (get-translation "summary.empty.title" "No dashboard items yet")]
     (when (:editable tab)
       [buttons/button {:primary? true
                        :icon     [icons/mux-reg-plus]
                        :text     (get-translation "dashboards.add-item" "Add Dashboard Item")
                        :href     (:new_item_url tab)}])]))

(defn- items-section []
  (let [items      @(re-frame/subscribe [::subs/field :items])
        reload-key @(re-frame/subscribe [::subs/field :reload-key])]
    (if (empty? items)
      [empty-state]
      [cards-grid items reload-key])))

(defn page []
  (let [loading?    @(re-frame/subscribe [::subs/field :loading?])
        load-error? @(re-frame/subscribe [::subs/field :load-error?])
        tabs        @(re-frame/subscribe [::subs/field :tabs])]
    [:div.summary-page
     (cond
       (and loading? (empty? tabs))
       [:div.summary-loading [spinners/mux-spinner]]

       (and load-error? (empty? tabs))
       [:div.summary-empty
        [:span (get-translation "generic.error.general" "Something went wrong. Please try again.")]
        [buttons/button {:text     (get-translation "generic.button.retry" "Retry")
                         :on-click #(re-frame/dispatch [::events/fetch nil])}]]

       :else
       [:<>
        [tab-bar]
        [toolbar]
        [:div.summary-body {:class (when loading? "loading")}
         [items-section]]])
     [dashboard-tab-modal/tab-edit-modal
      {:db-key             events/db-key
       :on-created         [::events/tab-saved :select
                            (get-translation "dashboards.notify.created" "The dashboard tab was created.")]
       :on-saved           [::events/tab-saved nil
                            (get-translation "dashboards.notify.updated" "The dashboard tab was updated.")]
       :on-key-regenerated [::events/key-regenerated]
       :on-failure         [::events/request-failed]}]]))
